import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const API_URL = process.env.REACT_APP_API_URL || 'https://ijbc-backend-1089361061119.us-west1.run.app';

interface Announcement {
  id: string;
  title: string;
  date: string;
  content: string;
  author: string;
}

const NewsContainer = styled.div`
  min-height: 100vh;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #2c5aa0 0%, #1e3a8a 100%);
  color: #fff;
  padding: 80px 0;
  text-align: center;

  @media (max-width: 768px) {
    padding: 60px 0;
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const Title = styled.h1`
  font-size: 48px;
  font-weight: 700;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const Subtitle = styled.p`
  font-size: 20px;
  opacity: 0.9;
  max-width: 600px;
  margin: 0 auto;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const NewsSection = styled.section`
  padding: 60px 0;
  background: #f8f9fa;

  @media (max-width: 768px) {
    padding: 40px 0;
  }
`;

const Inner = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 0 20px;
`;

const NoticeTable = styled.div`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.07);
  overflow: hidden;
`;

const NoticeRow = styled.div<{ $open: boolean }>`
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background 0.2s ease;
  background: ${({ $open }) => ($open ? '#f0f5ff' : '#fff')};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${({ $open }) => ($open ? '#e8f0fe' : '#f8f9fa')};
  }
`;

const NoticeHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 18px 24px;
  gap: 16px;

  @media (max-width: 600px) {
    padding: 14px 16px;
    gap: 10px;
  }
`;

const NoticeDate = styled.span`
  color: #888;
  font-size: 13px;
  white-space: nowrap;
  flex-shrink: 0;
`;

const NoticeTitle = styled.span<{ $open: boolean }>`
  flex: 1;
  font-size: 16px;
  font-weight: ${({ $open }) => ($open ? '700' : '500')};
  color: ${({ $open }) => ($open ? '#2c5aa0' : '#333')};
  line-height: 1.4;

  @media (max-width: 600px) {
    font-size: 14px;
  }
`;

const Chevron = styled.span<{ $open: boolean }>`
  color: #aaa;
  font-size: 12px;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0)')};
  transition: transform 0.2s ease;
  flex-shrink: 0;
`;

const NoticeBody = styled.div<{ $open: boolean }>`
  display: ${({ $open }) => ($open ? 'block' : 'none')};
  padding: 0 24px 24px 24px;
  border-top: 1px solid #e8f0fe;

  @media (max-width: 600px) {
    padding: 0 16px 16px 16px;
  }
`;

const NoticeContent = styled.div`
  font-size: 15px;
  color: #444;
  line-height: 1.8;
  margin-top: 16px;
  white-space: pre-wrap;
  word-break: break-word;

  img {
    max-width: 100%;
    border-radius: 8px;
    margin: 8px 0;
    display: block;
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 32px;
  flex-wrap: wrap;
`;

const PageBtn = styled.button<{ $active?: boolean }>`
  padding: 8px 14px;
  border-radius: 6px;
  border: 1px solid ${({ $active }) => ($active ? '#2c5aa0' : '#dee2e6')};
  background: ${({ $active }) => ($active ? '#2c5aa0' : '#fff')};
  color: ${({ $active }) => ($active ? '#fff' : '#333')};
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ $active }) => ($active ? '#1e3a8a' : '#f0f4ff')};
    border-color: #2c5aa0;
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

const LoadingMsg = styled.div`
  text-align: center;
  padding: 60px 0;
  color: #888;
  font-size: 16px;
`;

const PAGE_SIZE = 15;

const GCS_IMG_RE = /(https:\/\/storage\.googleapis\.com\/[^\s]+\.(?:png|jpg|jpeg|gif|webp))/gi;

// Render content: split on GCS image URLs wherever they appear (inline or on own line)
function renderContent(content: string) {
  const parts = content.split(GCS_IMG_RE);
  return parts.map((part, i) => {
    if (GCS_IMG_RE.test(part)) {
      GCS_IMG_RE.lastIndex = 0;
      return <img key={i} src={part} alt="" style={{ maxWidth: '100%', borderRadius: 8, margin: '8px 0', display: 'block' }} />;
    }
    GCS_IMG_RE.lastIndex = 0;
    return <span key={i} style={{ whiteSpace: 'pre-wrap' }}>{part}</span>;
  });
}

const News: React.FC = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    fetch(`${API_URL}/api/announcements`)
      .then(r => r.json())
      .then(data => {
        if (data.success) setAnnouncements(data.announcements);
      })
      .catch(err => console.error('Failed to fetch announcements:', err))
      .finally(() => setLoading(false));
  }, []);

  const totalPages = Math.ceil(announcements.length / PAGE_SIZE);
  const pageItems = announcements.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const toggle = (id: string) => setOpenId(prev => (prev === id ? null : id));

  const goPage = (p: number) => {
    setPage(p);
    setOpenId(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const pageNumbers = () => {
    const range: number[] = [];
    const start = Math.max(1, page - 2);
    const end = Math.min(totalPages, page + 2);
    for (let i = start; i <= end; i++) range.push(i);
    return range;
  };

  return (
    <NewsContainer>
      <HeroSection>
        <HeroContent>
          <Title>공지사항</Title>
          <Subtitle>얼바인 중앙침례교회의 최신 소식과 공지사항을 확인하세요</Subtitle>
        </HeroContent>
      </HeroSection>

      <NewsSection>
        <Inner>
          {loading ? (
            <LoadingMsg>불러오는 중...</LoadingMsg>
          ) : announcements.length === 0 ? (
            <LoadingMsg>등록된 공지사항이 없습니다.</LoadingMsg>
          ) : (
            <>
              <NoticeTable>
                {pageItems.map((item) => {
                  const isOpen = openId === item.id;
                  return (
                    <NoticeRow key={item.id} $open={isOpen} onClick={() => toggle(item.id)}>
                      <NoticeHeader>
                        <NoticeDate>{item.date}</NoticeDate>
                        <NoticeTitle $open={isOpen}>{item.title}</NoticeTitle>
                        <Chevron $open={isOpen}>▼</Chevron>
                      </NoticeHeader>
                      <NoticeBody $open={isOpen}>
                        <NoticeContent>{renderContent(item.content)}</NoticeContent>
                      </NoticeBody>
                    </NoticeRow>
                  );
                })}
              </NoticeTable>

              {totalPages > 1 && (
                <Pagination>
                  <PageBtn onClick={() => goPage(1)} disabled={page === 1}>«</PageBtn>
                  <PageBtn onClick={() => goPage(page - 1)} disabled={page === 1}>‹</PageBtn>
                  {pageNumbers().map(p => (
                    <PageBtn key={p} $active={p === page} onClick={() => goPage(p)}>{p}</PageBtn>
                  ))}
                  <PageBtn onClick={() => goPage(page + 1)} disabled={page === totalPages}>›</PageBtn>
                  <PageBtn onClick={() => goPage(totalPages)} disabled={page === totalPages}>»</PageBtn>
                </Pagination>
              )}
            </>
          )}
        </Inner>
      </NewsSection>
    </NewsContainer>
  );
};

export default News;
